// Private leagues — operator oversight list (search + status filter +
// pagination). Rows open the detail page (owner + member ranking).
//
// Routing note: `/leagues/$id` is a file-based child, so TSR needs <Outlet />
// for the detail page to mount — same conditional-outlet pattern users.tsx
// uses.
import { createFileRoute, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { fetchLeaguesAdmin, keys } from "@/lib/queries";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { Pagination } from "@/components/ui/Pagination";
import { Avatar } from "@/components/ui/Avatar";
import { usePagination } from "@/lib/usePagination";
import { fmtRelative } from "@/lib/format";

export const Route = createFileRoute("/_app/leagues")({
  component: LeaguesPage,
});

function LeaguesPage() {
  const { location } = useRouterState();
  if (location.pathname !== "/leagues") return <Outlet />;
  return <LeaguesListPage />;
}

function LeaguesListPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const { page, setPage, limit, offset } = usePagination();

  // Debounce the search box so we don't fire a request per keystroke.
  const [dq, setDQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  // Narrowing the result set must reset to page 0.
  useEffect(() => {
    setPage(0);
  }, [dq, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const leagues = useQuery({
    queryKey: keys.leaguesList(dq || undefined, status || undefined, limit, offset),
    queryFn: () =>
      fetchLeaguesAdmin({ limit, offset, q: dq || undefined, status: status || undefined }),
  });

  const open = (id: string) => navigate({ to: "/leagues/$id", params: { id } });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Private leagues</h1>
        <p className="text-sm text-muted">
          Every private league. Open a row to see its owner and member ranking.
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[260px] flex-1">
          <Input placeholder="Search by league name…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="w-48">
          <Select
            placeholder="Status — active & closed"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={[
              { value: "ACTIVE", label: "Active" },
              { value: "CLOSED", label: "Closed" },
              { value: "DELETED", label: "Deleted" },
            ]}
          />
        </div>
        {(q || status) && (
          <Button
            variant="ghost"
            onClick={() => {
              setQ("");
              setStatus("");
            }}
          >
            Reset
          </Button>
        )}
      </div>
      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Leagues</CardTitle>
          <span className="text-2xs text-muted">{leagues.data?.page.total ?? 0}</span>
        </CardHeader>
        <CardBody className="p-0">
          {leagues.isLoading && (
            <div className="space-y-2 p-5">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          )}
          {leagues.data && leagues.data.items.length === 0 && (
            <p className="px-5 py-16 text-center text-sm text-muted">No leagues match the current filters.</p>
          )}
          {leagues.data && leagues.data.items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className="px-5 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted">League</th>
                    <th className="px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted">Owner</th>
                    <th className="px-3 py-3 text-right text-2xs font-medium uppercase tracking-wide text-muted">Members</th>
                    <th className="px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted">Status</th>
                    <th className="px-5 py-3 text-right text-2xs font-medium uppercase tracking-wide text-muted">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {leagues.data.items.map((lg) => (
                    <tr
                      key={lg.league_id}
                      onClick={() => open(lg.league_id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          open(lg.league_id);
                        }
                      }}
                      tabIndex={0}
                      role="link"
                      aria-label={`Open ${lg.name}`}
                      className="cursor-pointer border-b border-border/60 transition-colors last:border-0 hover:bg-neon/5 focus:bg-neon/5 focus:outline-none"
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar url={lg.image_url} name={lg.name} />
                          <div className="min-w-0">
                            <div className="truncate text-xs font-medium text-fg">{lg.name}</div>
                            <div className="truncate font-mono text-2xs text-muted">{lg.league_id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-xs text-fg">
                        {lg.owner_name || (
                          <span className="font-mono text-2xs text-muted">{lg.owner_id}</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right text-xs tabular-nums text-fg">{lg.member_count}</td>
                      <td className="px-3 py-3">
                        <Badge tone={leagueTone(lg.status)}>{lg.status}</Badge>
                      </td>
                      <td className="px-5 py-3 text-right text-2xs text-muted">{fmtRelative(lg.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {leagues.data && (
            <Pagination
              page={leagues.data.page}
              currentPage={page}
              onPageChange={setPage}
              itemLabel="leagues"
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function leagueTone(s: string): "success" | "muted" | "danger" {
  if (s === "ACTIVE") return "success";
  if (s === "DELETED") return "danger";
  return "muted";
}
